import Vue from 'vue';
import {
  localize, extend, ValidationObserver, ValidationProvider, setInteractionMode,
} from 'vee-validate';
import lodash from 'lodash';
import * as rules from 'vee-validate/dist/rules';
import en from './messages';

const VALIDATION_MODE = 'lazy';
const URL_REGEXP = /^https?:\/\/(?:[a-zA-Z\d][-a-zA-Z\d]{0,61}[a-zA-Z\d]?\.)+[a-zA-Z]{2,63}\/?$|^https?:\/\/(?:[a-zA-Z\d][-a-zA-Z\d]{0,61}[a-zA-Z\d]?\.)+[a-zA-Z]{2,63}(?:\/[-a-zA-Z.\d_~!$&'()*+,;=@%?#]+)*\/?$/;
const URL_REGEXP_WITHOUT_PROTOCOL = /^(https?:\/\/)?(?:[a-zA-Z\d][-a-zA-Z\d]{0,61}[a-zA-Z\d]?\.)+[a-zA-Z]{2,63}\/?$|^(https?:\/\/)?(?:[a-zA-Z\d][-a-zA-Z\d]{0,61}[a-zA-Z\d]?\.)+[a-zA-Z]{2,63}(?:\/[-a-zA-Z.\d_~!$&'()*+,;=@%?#]+)*\/?$/;
const PASSWORD_REGEXP = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z~!@#$%&()\-_+={}[\]|\\;:<>,./?\d]{8,}$/;
const PHONE_REGEXP = /^\d{10}$/;
const EXT_PHONE_REGEXP = /^\d{0,6}$/;
const PHONE_WITH_MASK_REGEXP = /^\d{3}-\d{3}-\d{4}$/;
const PHONE_WITH_MASK_COUNTRY_CODE_REGEXP = /^\+1 \d{3}-\d{3}-\d{4}$/;
const EMAIL_REGEXP = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/i;
const NAME_REGEXP = /^[A-Za-z.\-\x27\x20\u2018\u2019\u0027\u0060]+$/;
const CARD_EXPIRATION_REGEXP = /^\d{2}\/\d{2}$/;
const CARD_NUMBER_REGEXP = /^\d{4}\s?\d{4}\s?\d{4}\s?\d(\d{0,3}(\s?\d{0,3})?)?/;
const ACCOUNT_NUMBER_REGEXP = /^\d{4,17}$/;
const ROUTING_NUMBER_REGEXP = /^\d{9}$/;
const ZIP_CODE_REGEXP = /^\d{5}$/;


/** Register global vee help elements */
Vue.component('ValidationObserver', ValidationObserver);
Vue.component('ValidationProvider', ValidationProvider);

localize({ en });
setInteractionMode(VALIDATION_MODE);

/** Load all vee rules */
lodash.map(rules, (rule, key) => extend(key, rule));

/**
 * ======================== Vee extend rules =======================
 */

/**
 * Validate name
 */
extend('name', {
  ...rules.regex,
  validate: (value) => NAME_REGEXP.test(value),
  message: 'Please use letters A to Z, dash, apostrophe, period or space only.',
});

/**
 * max length
 */
extend('maxLength', {
  ...rules.regex,
  validate: (value, params) => value.length <= params.length,
  params: ['length'],
  message: (value, params) => `Enter ${params.length} symbols maximum`,
});

/**
 * Validate URL
 */
extend('url', {
  ...rules.regex,
  validate: (value) => URL_REGEXP.test(value),
});

/**
 * Validate URL without protocol
 */
extend('url_without_protocol', {
  ...rules.regex,
  validate: (value) => URL_REGEXP_WITHOUT_PROTOCOL.test(value),
  message: en.messages.url,
});

/**
 * Validate password
 */
extend('password', {
  ...rules.regex,
  validate: (value) => PASSWORD_REGEXP.test(value),
});

/**
 * Validate phone number
 */
extend('phone', {
  ...rules.digits,
  validate: (value) => (value ? PHONE_REGEXP.test(value) : false),
});
extend('phone_with_mask', {
  ...rules.regex,
  validate: (value) => (value ? PHONE_WITH_MASK_REGEXP.test(value) : false),
  message: en.messages.phone,
});
extend('phone_with_mask_country_code', {
  ...rules.regex,
  validate: (value) => (value ? PHONE_WITH_MASK_COUNTRY_CODE_REGEXP.test(value) : false),
  message: en.messages.phone,
});
extend('ext_phone', {
  ...rules.digits,
  validate: (value) => (value ? EXT_PHONE_REGEXP.test(value) : false),
});

extend('onlyOne', {
  validate: (first, [params]) => !(String(first).length > 0 && String(params).length > 0),
  message: 'Only one of the fields "Referral ID" and "Partner ID" can be filled in',
});

extend('requiredIfNotChecked', {
  validate: (value, arg) => value || arg.arg,
  params: ['arg'],
  message: 'Tier(s) or Section(s) should be checked',
});
extend('requiredRecipient', {
  validate: (value, { arg1, arg2 }) => value || arg1 || arg2,
  params: ['arg1', 'arg2'],
  message: 'Recipient should be checked',
});
extend('card_expiration', {
  ...rules.regex,
  validate: (value) => {
    const res = (value ? CARD_EXPIRATION_REGEXP.test(value) : false);
    if (!res) {
      return false;
    }
    const month = parseInt(value.substring(0, 2), 10);
    const year = parseInt(value.substring(3), 10);
    const dt = new Date();

    if (month > 12) {
      return false;
    }

    if (year < Number(dt.getFullYear().toString().substring(2))) {
      return false;
    }

    if (year > Number(dt.getFullYear().toString().substring(2))) {
      return true;
    }

    if (month < (dt.getMonth() + 1)) {
      return false;
    }
    return res;
  },
});

extend('card_number', {
  ...rules.digits,
  validate: (value) => (value ? CARD_NUMBER_REGEXP.test(value) : false),
});

extend('account_number', {
  ...rules.digits,
  validate: (value) => (value ? ACCOUNT_NUMBER_REGEXP.test(value) : false),
  message: 'Please enter a 4-17 digit number',
});

extend('routing_number', {
  ...rules.digits,
  validate: (value) => (value ? ROUTING_NUMBER_REGEXP.test(value) : false),
  message: 'Please enter a 9-digit number',
});

extend('zip_code', {
  ...rules.digits,
  validate: (value) => (value ? ZIP_CODE_REGEXP.test(value) : false),
  message: 'Please enter a 5-digit number',
});

extend('json', {
  validate: (value) => {
    try {
      return JSON.parse(value, (key, val) => typeof val === 'object');
    } catch (error) {
      return false;
    }
  },
  message: 'Please input a valid json',
});

extend('requiredEmail', {
  ...rules.required,
  message: 'This field is required if E-mail is not provided.',
});

extend('requiredPhone', {
  ...rules.required,
  message: 'This field is required if Phone is not provided.',
});

extend('min_password_length', {
  ...rules.min,
  // validate: (value) => (value ? /^\d{4,17}$/.test(value) : false),
  message: 'Must be at least 8 characters long',
});

extend('lower_case_letter', {
  ...rules,
  validate: (value) => (value ? /[a-z]+/.test(value) : false),
  message: 'Minimum of 1 lower case letter [a-z]',
});
extend('upper_case_letter', {
  ...rules,
  validate: (value) => (value ? /[A-Z]+/.test(value) : false),
  message: 'Minimum of 1 upper case letter [A-Z]',
});
extend('numeric_letter', {
  ...rules,
  validate: (value) => (value ? /[0-9]+/.test(value) : false),
  message: 'Minimum of 1 numeric letter [0-9]',
});
extend('without_spaces', {
  ...rules,
  validate: (value) => (value ? !/\s+/.test(value) : false),
});
extend('confirmed', {
  ...rules.confirmed,
  message: 'Passwords must match',
});
extend('email', {
  validate: (value) => (value ? EMAIL_REGEXP.test(value) : false),
  message: 'The Email is in an invalid format',
});
extend('latin_characters_digits_spaces', (value) => {
  const notAllowedCharacters = value.replace(/[A-Za-z0-9 ]+/g, '').split('').join(' ');

  if (notAllowedCharacters) {
    return `Invalid character ${notAllowedCharacters}`;
  }

  return true;
});

/**
 * ======================== Custom rules =======================
 * @ATTENTION vee provide async validate, if you want sync validate
 * do it yourself mother ducker =)
 */
/**
 * Check is email
 * @param value
 * @returns {boolean}
 */
export default function isEmail(value) {
  return EMAIL_REGEXP.test(value);
}
